import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import ImageUpload from '../components/ImageUpload';
import SolutionDisplay from '../components/SolutionDisplay';
import Features from '../components/Features';
import { solveFromImage } from '../services/groqService';
import { fetchRemoteConfig } from '../services/remoteConfig';
import { logImageUpload, logSolutionRequest, logSolutionSuccess, logSolutionError } from '../services/analytics';

const Home = () => {
    const [uploadedImage, setUploadedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [solution, setSolution] = useState(null);
    const [error, setError] = useState(null);

    // Fetch Remote Config on component mount
    useEffect(() => {
        const initConfig = async () => {
            await fetchRemoteConfig();
        };
        initConfig();
    }, []);

    const handleImageUpload = (imageData) => {
        setUploadedImage(imageData);
        setSolution(null);
        setError(null);

        // Log image upload event
        logImageUpload(
            imageData.method || 'file',
            imageData.file?.size || 0
        );
    };

    const handleRemoveImage = () => {
        setUploadedImage(null);
        setSolution(null);
        setIsLoading(false);
        setError(null);
    };

    const handleFindSolution = async () => {
        if (!uploadedImage) return;

        setIsLoading(true);
        setError(null);

        const startTime = Date.now();

        // Log solution request
        logSolutionRequest();

        try {
            console.log('🚀 Starting Groq AI math solving...');

            // Solve math problem using Groq AI
            const result = await solveFromImage(uploadedImage.file);

            console.log('✅ Solution received:', result);
            setSolution(result);

            // Log solution success with response time
            const responseTime = Date.now() - startTime;
            logSolutionSuccess(responseTime);

        } catch (err) {
            console.error('❌ Error solving problem:', err);
            setError(err.message || 'Failed to solve the problem. Please try again.');

            // Log solution error
            logSolutionError(err.message, err.name || 'Unknown');

            // Show error in UI
            setSolution({
                problem: "Error occurred",
                steps: [
                    "There was an error processing your request.",
                    "Please check:",
                    "1. Your API key is set in Firebase Remote Config",
                    "2. The image is clear and readable",
                    "3. Your internet connection is stable"
                ],
                answer: "Please try again"
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Free Math Solver - Upload Image & Get Instant Solutions | FreeImageMathSolver"
                description="Solve any math problem instantly by uploading a photo. Get step-by-step solutions for Algebra, Calculus, Geometry, Trigonometry. 100% free, no signup required."
                keywords="free math solver, AI math solver, photo math solver, image math solver, step-by-step math solutions, algebra solver, calculus solver, geometry solver, math homework help"
                canonical="/"
            />

            {/* Hero Section */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center mb-12">
                    <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Solve Any Math Problem
                        <br />
                        <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                            Instantly with AI
                        </span>
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-4">
                        Upload a photo of your math problem and get detailed, step-by-step solutions in seconds
                    </p>
                    <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>Algebra</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>Geometry</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>Calculus</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>Trigonometry</span>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="grid lg:grid-cols-2 gap-8 mb-12">
                    {/* Left: Image Upload */}
                    <div>
                        <ImageUpload
                            onImageUpload={handleImageUpload}
                            uploadedImage={uploadedImage}
                            onRemoveImage={handleRemoveImage}
                        />

                        {uploadedImage && !solution && (
                            <div className="mt-6 flex justify-center">
                                <button
                                    onClick={handleFindSolution}
                                    disabled={isLoading}
                                    className="flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold text-lg rounded-xl shadow-2xl hover:shadow-indigo-500/50 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    Find Solution
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Right: Solution Display */}
                    <div>
                        <SolutionDisplay
                            solution={solution}
                            isLoading={isLoading}
                            uploadedImage={uploadedImage}
                        />
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <Features />

            {/* How It Works Section */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
                    <p className="text-xl text-gray-600">Three simple steps to solve any math problem</p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    <div className="text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6 shadow-lg">
                            1
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">Upload Image</h3>
                        <p className="text-gray-600">
                            Take a photo or upload an image of your math problem from your device or camera
                        </p>
                    </div>

                    <div className="text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6 shadow-lg">
                            2
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">Click Find Solution</h3>
                        <p className="text-gray-600">
                            Our AI analyzes your problem and generates a detailed step-by-step solution
                        </p>
                    </div>

                    <div className="text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6 shadow-lg">
                            3
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">Get Your Answer</h3>
                        <p className="text-gray-600">
                            Review the solution and download it as PDF for future reference
                        </p>
                    </div>
                </div>
            </section>

            {/* JSON-LD Schema for SEO */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebApplication",
                    "name": "FreeImageMathSolver",
                    "url": "https://freeimagemathsolver.online",
                    "description": "Free AI-powered math solver that provides step-by-step solutions by analyzing uploaded images of math problems",
                    "applicationCategory": "EducationalApplication",
                    "offers": {
                        "@type": "Offer",
                        "price": "0",
                        "priceCurrency": "USD"
                    },
                    "featureList": [
                        "Upload image of math problem",
                        "AI-powered problem recognition",
                        "Step-by-step solutions",
                        "Support for Algebra, Calculus, Geometry, Trigonometry",
                        "Download solutions as PDF",
                        "100% free with no signup required"
                    ]
                })}
            </script>
        </>
    );
};

export default Home;
